import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { isSchemaRefusal } from "../../flatbuffers/api/flatBufferBaseQuery";

export type ProbeOutcome = "reachable" | "unreachable" | "timeout";

export const PROBE_TIMEOUT_MS = 4000;
const PROBE_TIMEOUT_SECONDS = Math.round(PROBE_TIMEOUT_MS / 1000);

const DEFAULT_PORTS: Record<string, string> = { "http:": "80", "https:": "443" };
const LOOPBACK_HOSTNAMES = new Set(["localhost", "127.0.0.1", "[::1]"]);

type PageLocation = Pick<Location, "protocol" | "origin">;

export type ConnectionDiagnosisInput = {
  method: string;
  requestUrl: string;
  page: PageLocation;
  online: boolean;
  probe: ProbeOutcome;
  configKey: string;
};

export type ConnectionDiagnosis = {
  summary: string;
  causes: string[];
  checks: string[];
  probeLabel: string;
};

export type UnsentRequestDiagnosis = {
  summary: string;
  causes: string[];
  checks: string[];
};

type Context = ConnectionDiagnosisInput & {
  target: URL;
  hostname: string;
  port: string;
  hostPort: string;
  origin: string;
  crossOrigin: boolean;
  mixedContent: boolean;
};

type Finding = { lead?: string; causes: string[]; checks: string[] };
type ProbeFinding = Finding & { lead: string; label: string };

const statusOf = (error: unknown): unknown =>
  typeof error === "object" && error !== null ? (error as { status?: unknown }).status : undefined;

// The browser got no usable reply to the login request (RTK Query's FETCH_ERROR / TIMEOUT_ERROR).
export const isConnectionFailure = (error: unknown): boolean => {
  const status = statusOf(error);
  return status === "FETCH_ERROR" || status === "TIMEOUT_ERROR";
};

// The server answered in full, but names an API schema whose verdict says this application cannot
// read the reply's type safely, so the FlatBuffer base query refused to decode it and returned a
// CUSTOM_ERROR tagged as a schema refusal. Something answered: this is not an unsent request.
export const isRefusedReply = (error: unknown): boolean =>
  statusOf(error) === "CUSTOM_ERROR" && isSchemaRefusal(error as FetchBaseQueryError);

// The request never left the browser. RTK Query builds the Request outside its try block, so an
// unusable API address (for example a mistyped port) is THROWN, not returned. The shared base
// query turns that into a CUSTOM_ERROR result (see eachAttempt in
// redux/retryingBaseQuery.ts); the same status is what the FlatBuffer base query returns when it
// cannot pack a body. Its schema refusal is the one CUSTOM_ERROR that is a reply, not unsent. A
// thrown error that reaches the hook uncaught is a serialised exception with no `status`, which
// is also unsent.
export const isUnsentRequest = (error: unknown): boolean => {
  if (typeof error !== "object" || error === null) {
    return false;
  }
  const status = statusOf(error);
  if (status === "CUSTOM_ERROR") {
    return !isRefusedReply(error);
  }
  return status === undefined && typeof (error as { message?: unknown }).message === "string";
};

const resolveTarget = (requestUrl: string, pageOrigin: string): URL => {
  try {
    return new URL(requestUrl, pageOrigin);
  } catch {
    return new URL(pageOrigin);
  }
};

export const probeServerReachability = async (
  requestUrl: string,
  pageOrigin: string,
  timeoutMs: number = PROBE_TIMEOUT_MS,
  fetchImpl?: typeof fetch,
): Promise<ProbeOutcome> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const doFetch = fetchImpl ?? fetch;
    await doFetch(resolveTarget(requestUrl, pageOrigin).origin, {
      mode: "no-cors",
      cache: "no-store",
      signal: controller.signal,
    });
    return "reachable";
  } catch {
    return controller.signal.aborted ? "timeout" : "unreachable";
  } finally {
    clearTimeout(timer);
  }
};

const portOf = (target: URL): string => target.port || DEFAULT_PORTS[target.protocol] || "";

export const describeServerAddress = (requestUrl: string, pageOrigin: string): string => {
  const target = resolveTarget(requestUrl, pageOrigin);
  return `${target.hostname}:${portOf(target)}`;
};

// Where a site's API address lives: the frontend container writes config.js from its environment
// each time it starts (book/src/deployment-addresses.md), so a fix needs a container restart.
const RESTART_HINT = "then restart the frontend container so config.js is written again";

export const describeUnsentRequest = (input: {
  requestUrl: string;
  configKey: string;
}): UnsentRequestDiagnosis => ({
  summary: `The browser could not build the login request for "${input.requestUrl}".`,
  causes: [
    `The API address read from ${input.configKey} may not be a valid address (for example a typo in the port, or a missing http:// or https://).`,
  ],
  checks: [
    `Correct ${input.configKey} in the frontend container's environment so it looks like https://host:port/api (or a path such as /api behind the web server), ${RESTART_HINT}.`,
  ],
});

export type ServerReplyInput = {
  error: unknown;
  method: string;
  requestUrl: string;
  page: PageLocation;
};

const GATEWAY_STATUSES = new Set([502, 503, 504]);

// The server answered with a failure whose status says more than its message does. A gateway
// status means the web server is up and the service behind it is not: the usual way an API outage
// shows on the recommended setup, where the API sits behind the web server that served this page.
// Any other status (a wrong password, a validation failure, a route the API does not offer) needs
// no analysis here: the server's message explains it, and guessing at a cause would send support
// to the wrong place.
export const describeServerReply = (input: ServerReplyInput): UnsentRequestDiagnosis | null => {
  const status = statusOf(input.error);
  if (typeof status !== "number" || !GATEWAY_STATUSES.has(status)) {
    return null;
  }
  const target = resolveTarget(input.requestUrl, input.page.origin);
  const hostPort = describeServerAddress(input.requestUrl, input.page.origin);
  return {
    summary: `The request (${input.method} ${target.href}) was answered with HTTP ${status} by ${hostPort}. A web server or gateway answered, but it reports that the service behind it did not give a usable reply.`,
    causes: [
      `The API service behind ${hostPort} may be stopped, restarting, overloaded or crashed. A web server such as nginx answers 502 when that service refuses or drops the connection and 504 when it does not answer in time; a 503 can also be the service's own reply.`,
      `The web server may forward ${target.pathname} to the wrong address or port.`,
      "If the server's own message above explains the error, that message is the cause.",
    ],
    checks: [
      "Confirm the API service is running and healthy on the server.",
      "Look in the web server's error log for the upstream error recorded when this request was made.",
      `Confirm the web server forwards ${target.pathname} to the port the API service actually listens on.`,
    ],
  };
};

// The server answered, but its API schema changed the reply's type in a way this application
// cannot read safely (`isRefusedReply`). The cause is a release mismatch, not the network or the
// address; the error text in the report names the type and what changed.
export const describeRefusedReply = (input: ServerReplyInput): UnsentRequestDiagnosis => {
  const target = resolveTarget(input.requestUrl, input.page.origin);
  const hostPort = describeServerAddress(input.requestUrl, input.page.origin);
  return {
    summary: `The request (${input.method} ${target.href}) was answered by ${hostPort}, but the server's API schema is not this application's and changes the reply's type in a way this application cannot read safely, so the reply was not read.`,
    causes: [
      "The frontend and the LiftNet API are different releases: one of them was updated without the other.",
      "The error text above names the type and what changed in it.",
    ],
    checks: [
      "Update the frontend and the LiftNet API to matching releases.",
      "Compare the two schema hashes in the error text with the versions each side is running.",
    ],
  };
};

const buildContext = (input: ConnectionDiagnosisInput): Context => {
  const target = resolveTarget(input.requestUrl, input.page.origin);
  const port = portOf(target);
  const isLoopback = LOOPBACK_HOSTNAMES.has(target.hostname);
  return {
    ...input,
    target,
    hostname: target.hostname,
    port,
    hostPort: `${target.hostname}:${port}`,
    origin: target.origin,
    crossOrigin: target.origin !== input.page.origin,
    mixedContent: input.page.protocol === "https:" && target.protocol === "http:" && !isLoopback,
  };
};

const OFFLINE_FINDING: Finding = {
  lead: "this computer has no network connection.",
  causes: ["This computer reports that it has no network connection."],
  checks: ["Check this computer's network cable or Wi-Fi connection."],
};

// The browser blocks this request before it reaches the network, so a reachability probe would be
// blocked the same way and says nothing about the server. This finding takes the probe's place.
const mixedContentFinding = (ctx: Context): ProbeFinding => ({
  lead: "the browser blocks an HTTPS page from calling an HTTP API.",
  causes: [
    `This page was loaded over HTTPS but the API address (${ctx.origin}) is plain HTTP, and browsers block that request.`,
  ],
  checks: [
    `Change the API address in ${ctx.configKey} to use https://, or serve this page over http://.`,
  ],
  label: "Not checked: the browser blocks this request before it leaves this computer.",
});

// Only for an API address that names somewhere other than this page. A relative address (the
// recommended setup, with the API behind the web server that served this page) is not the problem
// when that web server stops answering: a wrong relative path draws an HTTP status, not silence.
const configFinding = (ctx: Context): Finding => ({
  causes: [
    `The API address is the ${ctx.configKey} setting (written to config.js from the frontend container's environment when it starts) and currently points at ${ctx.origin}. It must name the host and port where the API actually runs.`,
  ],
  checks: [`Confirm ${ctx.configKey} names the correct host and port, ${RESTART_HINT}.`],
});

// What has to answer at the address: the API service itself, or, for a relative address, the web
// server that served this page with the API behind it.
const serviceOf = (ctx: Context): string => (ctx.crossOrigin ? "API service" : "web server");

const unreachableCauses = (ctx: Context): string[] => {
  const causes = [
    `No usable answer from ${ctx.hostPort}: the ${serviceOf(ctx)} may not be running, port ${ctx.port} may be closed or blocked by a firewall, or the browser withheld the reply.`,
    `This computer may have no network route to ${ctx.hostname} (wrong network, VLAN or VPN).`,
  ];
  // The usual edge setup is a relative /api behind the same web server that served this page, so
  // the server answered a moment ago; the likelier story is that it, or the network, went away.
  if (!ctx.crossOrigin) {
    causes.push(
      "This page was loaded from the same address, so the server or the network may have gone away since it loaded.",
    );
  }
  // For a same-origin API the certificate was accepted when the page itself loaded.
  if (ctx.target.protocol === "https:" && ctx.crossOrigin) {
    causes.push(
      `The certificate on ${ctx.hostPort} may not be trusted by this browser (common with self-signed site certificates). Open ${ctx.origin}/ in a new tab and accept the warning.`,
      `Port ${ctx.port} may be serving plain HTTP while the configured API address uses HTTPS.`,
    );
  }
  return causes;
};

const noAnswerChecks = (ctx: Context): string[] => [
  `In a new browser tab, open ${ctx.origin}/ and see whether anything loads. Any response, even an error page, means the server is reachable; no response means a network or service problem.`,
  `Confirm the ${serviceOf(ctx)} is running on ${ctx.hostname} and listening on port ${ctx.port}.`,
  `Confirm no firewall between this computer and ${ctx.hostname} blocks port ${ctx.port}.`,
];

// An HTTP reply (even a 404 or 502) would have reached the application as a status code, so
// reaching this finding means the login request itself drew no reply while the server answers now.
const reachableFinding = (ctx: Context): ProbeFinding => {
  const answered = `The server at ${ctx.hostPort} answered a basic request when checked, so the host and port are up now. The login request itself got no response.`;
  const cause = ctx.crossOrigin
    ? `${answered} Most likely the API does not allow requests from ${ctx.page.origin} (CORS); a content-security policy or browser extension could also block it, or the server may have been briefly unavailable when the login was tried.`
    : `${answered} Possible causes: a browser extension or security policy blocked it, a proxy or firewall dropped the connection, or the server was briefly unavailable when the login was tried.`;
  const checks = ctx.crossOrigin
    ? [
        `Make sure the API allows requests from ${ctx.page.origin} (CORS).`,
        "Try the login again; if it keeps failing, look for a CORS or security-policy message in the browser's developer console.",
      ]
    : [
        "Try the login again; if it keeps failing, disable browser extensions for this site and check proxy and firewall logs for the login request.",
      ];
  return {
    lead: "the server answers now, but the login request got no response: the browser blocked it (a CORS or security setting) or the server was briefly unavailable when it was tried.",
    causes: [cause],
    checks,
    label: `${ctx.hostPort} answered when checked, so the host and port are up now.`,
  };
};

const PROBE_FINDINGS: Record<ProbeOutcome, (ctx: Context) => ProbeFinding> = {
  unreachable: (ctx) => ({
    lead: `the ${serviceOf(ctx)} is down, port ${ctx.port} is blocked, or there is no network route to ${ctx.hostname}.`,
    causes: unreachableCauses(ctx),
    checks: noAnswerChecks(ctx),
    label: `${ctx.hostPort} gave no usable answer (connection refused, blocked, untrusted certificate, or a reply the browser withheld).`,
  }),
  timeout: (ctx) => ({
    lead: `a firewall is dropping traffic to ${ctx.hostPort}, or ${ctx.hostname} is not on this network.`,
    causes: [
      `No answer within ${PROBE_TIMEOUT_SECONDS}s: a firewall is probably dropping traffic to ${ctx.hostPort}, or ${ctx.hostname} is powered off or on another network.`,
      `The ${serviceOf(ctx)} on ${ctx.hostname} may not be running.`,
    ],
    checks: noAnswerChecks(ctx),
    label: `${ctx.hostPort} did not answer within ${PROBE_TIMEOUT_SECONDS}s.`,
  }),
  reachable: reachableFinding,
};

export const describeConnectionFailure = (input: ConnectionDiagnosisInput): ConnectionDiagnosis => {
  const ctx = buildContext(input);
  const probeFinding = ctx.mixedContent ? mixedContentFinding(ctx) : PROBE_FINDINGS[ctx.probe](ctx);
  const findings: Finding[] = [];
  if (!ctx.online) {
    findings.push(OFFLINE_FINDING);
  }
  findings.push(probeFinding);
  if (ctx.crossOrigin) {
    findings.push(configFinding(ctx));
  }
  const lead = findings.find((finding) => finding.lead)?.lead ?? probeFinding.lead;
  return {
    summary: `The login request (${ctx.method} ${ctx.target.href}) got no response. Most likely: ${lead}`,
    causes: findings.flatMap((finding) => finding.causes),
    checks: findings.flatMap((finding) => finding.checks),
    probeLabel: probeFinding.label,
  };
};
